/**
@format
@flow
*/
import React, { useEffect } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import {
  Route, Switch, Redirect,
} from 'react-router-dom';
import styled from 'styled-components';
import { NavBar, SideNavBar } from '@ziynx/components/navigation';
import { Button } from '@ziynx/components/buttons';
import Icon from '@ziynx/components/icons';
import ActionTypes from '@ziynx/action-types';
import LoginScreen from '@ziynx/pages/Login/Login';
import SignUpScreen from '@ziynx/pages/SignUp/SignUp';
import ProfileSetupScreen from '@ziynx/pages/SignUp/ProfileSetup';
import ConfirmCodeScreen from '@ziynx/pages/SignUp/ConfirmCode';
import PrivacyPolicyScreen from '@ziynx/pages/SignUp/PrivacyPolicy';
import ConfirmEmailScreen from '@ziynx/pages/ForgotPassword/ConfirmEmail';
import ResetPasswordScreen from '@ziynx/pages/ForgotPassword/ResetPassword';

// AUTHED
import TermsOfServiceScreen from '@ziynx/pages/SignUp/TermsOfService';
import AccountSettingsScreen from '@ziynx/pages/Account/AccountSettings';
import EditPersonalInfoScreen from '@ziynx/pages/Account/EditPersonalInfo';
import ChangePasswordScreen from '@ziynx/pages/Account/ChangePassword';
import SupportIntakeScreen from '@ziynx/pages/Account/SupportIntake';

// DISCOVER
import TalentProfileScreen from '@ziynx/pages/Discover/TalentProfile';
import DiscoverScreen from '@ziynx/pages/Discover/Discover';
import ChannelScreen from '@ziynx/pages/Discover/Channel';
import WatchlistScreen from '@ziynx/pages/Discover/Watchlist';
import FilterScreen from '@ziynx/pages/Discover/Filter';

// TEAM
import TeamScreen from '@ziynx/pages/Team/Team';
import EditTeamMemberScreen from '@ziynx/pages/Team/EditTeamMember';
import InviteTeamMemberScreen from '@ziynx/pages/Team/InviteTeamMember';
import InvitedSignupScreen from '@ziynx/pages/Team/InvitedSignup';

// CONTENT
import Content from '@ziynx/pages/Content/Content';
import VideoQuestion from '@ziynx/pages/Content/VideoQuestion';
import VideoRepliesScreen from '@ziynx/pages/Content/VideoReplies';
import TextStatement from '@ziynx/pages/Content/TextStatement';

// JOBS
import JobsScreen from '@ziynx/pages/Jobs/Jobs';
import JobPostScreen from '@ziynx/pages/Jobs/JobPost';

// COMPANY PROFILE
import CompanyProfileScreen from '@ziynx/pages/CompanyProfile/Profile';
import EditProfileScreen from '@ziynx/pages/CompanyProfile/EditProfile';
import EditVideoScreen from '@ziynx/pages/CompanyProfile/EditVideos';

import PageNotFound from '@ziynx/pages/PageNotFound';
import ComingSoonScreen from '@ziynx/pages/ComingSoon';

// ADMIN
import AdminTalentListScreen from '@ziynx/pages/Admin';
import AdminEditTalentScreen from '@ziynx/pages/Admin/EditTalent';
import AdminTalentListFilterScreen from '@ziynx/pages/Admin/TalentList/filter';

// NIL
import NILListScreen from '@ziynx/pages/NIL/List';
import NILCreateScreen from '@ziynx/pages/NIL/Create';
import NILApplicationsScreen from '@ziynx/pages/NIL/Applications';

// ORIGINALS
import OriginalsListScreen from '@ziynx/pages/Originals/List';
import OriginalCreateScreen from '@ziynx/pages/Originals/Create';

// REDUX
import type { AuthState } from '@ziynx/flow-types/auth';
import ScrollToTop, { getInitials } from '@ziynx/components/utilities';
import { sidebarContent, adminSidebarContent } from './sidebarContent';

// While the platform is being transformed, signed-in users only see /coming-soon.
// Set to false to restore the full app.
const COMING_SOON_MODE = true;

type ProtectedRouteProps = {
  component: any,
  exact: boolean,
  path: string,
  type?: 'auth' | 'private' | 'redirect' | 'admin',
  redirectTo?: string,
};

type User = {
  given_name: string,
  family_name: string,
  fetching: boolean,
  error: any,
  email: string,
  email_verified: boolean,
  name: string,
  refreshToken: boolean,
  company?: {_id: string, name: string},
  // fetching: boolean,
  success: boolean,
  error: any,
  userInfo: {
    firstName: string,
    lastName: string,
  },
  profileImage: {
    sources: {
      cropped: {
        url: string,
      },
      optimized: {
        url: string,
      },
      original: {
        url: string,
      }
    }
  }
}

type DispatchType = {
  type: string,
  id?: string,

} => void

const StyledButton = styled.button`
background: none;
color: inherit;
border: none;
padding: 0;
font: inherit;
cursor: pointer;
outline: inherit;
  ::after {
    content: "";
    position: absolute;
    bottom: -8px;
    right: -12px;
    width: 42px;
    height: 42px;
  }
`;

/**
 * Acts as an app router using Switch and Routes
 */
export default function AppRouter(): React$Node {
  const authService: AuthState = useSelector(
    ({ auth }) => auth[ActionTypes.auth.checkIfLoggedIn.GROUP] || {},
    shallowEqual,
  );

  const dispatch: DispatchType = useDispatch();

  useEffect(() => {
    if (authService?.isLoggedIn) {
      dispatch({
        type: ActionTypes.user.init.START,
      });
    }
    // Clear all notifications on refresh
    dispatch({
      type: ActionTypes.notifications.removeAll,
    });
  }, [authService?.isLoggedIn]);

  const userService: User = useSelector(
    ({ user }) => user[ActionTypes.user.init.GROUP] || {}, shallowEqual,
  );

  const fullName = `${userService?.userInfo?.firstName} ${userService?.userInfo?.lastName}`;

  const logOut = async () => {
    dispatch({ type: ActionTypes.auth.logout.START });
  };

  function ProtectedRoute({
    exact, path, component, type, redirectTo,
  }: ProtectedRouteProps): React$Node {
    let _renderRedirect;
    let isAdmin;

    switch (type) {
    case 'auth':
      _renderRedirect = (): React$Node => <Redirect to="/" />;
      return (
        <Route
          exact={exact}
          path={path}
          {...!authService?.isLoggedIn ? { component } : { render: _renderRedirect }}
        />
      );
    case 'redirect':
      _renderRedirect = (): React$Node => <Redirect to={redirectTo} />;
      return (
        <Route
          exact={exact}
          path={path}
          render={_renderRedirect}
        />
      );
    case 'admin':
      _renderRedirect = ({ location }: { location: any }): React$Node => <Redirect to={{ pathname: '/', state: { from: location } }} />;
      isAdmin = authService?.isLoggedIn && authService?.isAdmin;
      return (
        <Route
          exact={exact}
          path={path}
          {...isAdmin ? { component } : { render: _renderRedirect }}
        />
      );
    case 'private':
    default:
      _renderRedirect = ({ location }: { location: any }): React$Node => <Redirect to={{ pathname: '/', state: { from: location } }} />;
      return (
        <Route
          exact={exact}
          path={path}
          {...authService?.isLoggedIn ? { component } : { render: _renderRedirect }}
        />
      );
    }
  }

  if (COMING_SOON_MODE && authService?.isLoggedIn) {
    return (
      <Switch>
        <Route
          exact
          path="/coming-soon"
          component={ComingSoonScreen}
        />
        <Redirect from="*" to="/coming-soon" />
      </Switch>
    );
  }

  return (
    <>
      {authService?.isLoggedIn && (
        <>
          <NavBar
            variant="loggedIn"
            offsetLeft={68}
          />
          <SideNavBar
            content={[
              ...sidebarContent,
              ...(authService?.isAdmin ? adminSidebarContent : []),
            ]}
            logoutOnClick={logOut}
            expandedBottomAction={{
              popover: {
                // eslint-disable-next-line jsx-a11y/control-has-associated-label
                trigger: <StyledButton type="button"><Icon name="alerts" tiny /></StyledButton>,
                position: 'topRight',
                children: <Button variant="primary" onClick={logOut}>Log Out</Button>,
              },
            }}
            topContent={(!userService.fetching && userService?.success)
              ? {
                image: {
                  uri: userService?.profileImage?.sources?.optimized?.url
                  || userService?.profileImage?.sources?.cropped?.url
                  || userService?.profileImage?.sources?.original?.url,
                  placeholderText: getInitials(fullName),
                },
                heading: {
                  children: fullName,
                },
                subHeading: {
                  children: userService?.name ?? userService?.company?.name,
                },
              } : undefined}
          />
        </>
      )}
      {!authService?.isLoggedIn && (
        <Switch>
          <Route
            exact
            path="/"
            component={LoginScreen}
          />
          <Route
            exact
            path="/discover/talent/:talentId"
            component={TalentProfileScreen}
          />
          <ProtectedRoute
            exact
            type="auth"
            path="/sign-up"
            component={SignUpScreen}
          />
          <ProtectedRoute
            exact
            type="auth"
            path="/confirm-code"
            component={ConfirmCodeScreen}
          />
          <ProtectedRoute
            exact
            type="auth"
            path="/confirm-email"
            component={ConfirmEmailScreen}
          />
          <ProtectedRoute
            exact
            type="auth"
            path="/reset-password"
            component={ResetPasswordScreen}
          />
          <ProtectedRoute
            exact
            type="auth"
            path="/invite/:memberId"
            component={InvitedSignupScreen}
          />
          <Redirect from="*" to="/" />
        </Switch>
      )}
      {authService?.isLoggedIn && authService?.companyGroups?.length > 0 && (
        <>
          <ScrollToTop />
          <Switch>
            <ProtectedRoute
              exact
              type="auth"
              path="/privacy-policy"
              component={PrivacyPolicyScreen}
            />
            <ProtectedRoute
              exact
              type="auth"
              path="/terms-of-service"
              component={TermsOfServiceScreen}
            />
            <ProtectedRoute
              exact
              type="admin"
              path="/admin/:tab"
              component={AdminTalentListScreen}
            />
            <ProtectedRoute
              exact
              type="admin"
              path="/admin/talent/filter"
              component={AdminTalentListFilterScreen}
            />
            <ProtectedRoute
              exact
              type="admin"
              path="/admin/talent/:talentId"
              component={AdminEditTalentScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/discover"
              component={DiscoverScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/discover/talent/:talentId"
              component={TalentProfileScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/discover/channel/:channelId"
              component={ChannelScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/discover/watchlist"
              component={WatchlistScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/discover/filter"
              component={FilterScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/company-profile"
              component={CompanyProfileScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/company-profile/edit"
              component={EditProfileScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/company-profile/edit/videos"
              component={EditVideoScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/content"
              component={Content}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/content/video-question"
              component={VideoQuestion}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/content/video-question/:contentId"
              component={VideoQuestion}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/content/video-question/:contentId/replies"
              component={VideoRepliesScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/content/text-statement/"
              component={TextStatement}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/content/text-question/:contentId"
              component={TextStatement}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/nil"
              component={NILListScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/nil/:id/edit"
              component={NILCreateScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/nil/create"
              component={NILCreateScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/nil/:id/applications"
              component={NILApplicationsScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/originals"
              component={OriginalsListScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/originals/:id/edit"
              component={OriginalCreateScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/originals/create"
              component={OriginalCreateScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/jobs"
              component={JobsScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/jobs/create-post"
              component={JobPostScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/jobs/create-post/:jobId"
              component={JobPostScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/settings"
              component={AccountSettingsScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/settings/personal-info/edit"
              component={EditPersonalInfoScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/settings/change-password"
              component={ChangePasswordScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/support"
              component={SupportIntakeScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/team"
              component={TeamScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/team/edit-member/:memberId"
              component={EditTeamMemberScreen}
            />
            <ProtectedRoute
              exact
              type="private"
              path="/team/invite-member"
              component={InviteTeamMemberScreen}
            />
            <Redirect from="/" to="/discover" />
            <Route path="*">
              <PageNotFound />
            </Route>
          </Switch>
        </>
      )}
      {authService?.isLoggedIn && !authService?.companyGroups?.length && (
        <Switch>
          <Route
            exact
            path="/profile-setup"
            component={ProfileSetupScreen}
          />
          <Redirect from="*" to="/profile-setup" />
        </Switch>
      )}
    </>
  );
}
